import type { ComponentPublicInstance } from 'vue'

import type { Agent } from '@newrelic/browser-agent/loaders/agent'
import type { AxiosError } from 'axios'

import {
  getErrorMessage,
  getErrorStack,
  isErrorLike,
  isNoise,
  type NormalizedError
} from './errorNoiseFilters'

/**
 * Coerce an arbitrary thrown value into an `Error` New Relic can report.
 *
 * A real `Error` passes straight through. Anything else gets wrapped, and the
 * wrapper inherits the original `stack` when there is one — without that,
 * `new Error(...)` re-roots the trace at this handler and every such error in
 * New Relic points at `AppErrorService` instead of wherever it was thrown.
 * That matters most for errors from another realm (an injected third-party
 * tag, an extension, an iframe), which fail `instanceof Error` in ours despite
 * being perfectly ordinary errors.
 */
function toReportableError(reason: unknown, message: string): Error {
  if (reason instanceof Error) return reason

  const error = new Error(message)
  const stack = getErrorStack(reason)
  if (stack) error.stack = stack

  return error
}

// Errors this service has handed to the Browser Agent. The agent runs its
// `setErrorHandler` filter over `noticeError` calls as well as over its own
// auto-capture, passing the same object through either way, so membership here
// is what lets `main.ts` tell our report apart from the agent's duplicate of
// the same throw. A `WeakSet` keeps the marker off the error objects
// themselves, and entries are never removed — the agent may replay a buffered
// error long after `noticeError` returns, once the `jserrors` aggregate chunk
// has loaded.
const reportedErrors = new WeakSet<object>()

/** Whether `error` reached the Browser Agent through `appErrorService`. */
export function wasReportedByAppErrorService(error: unknown): boolean {
  return typeof error === 'object' && error !== null && reportedErrors.has(error)
}

// Axios errors the Sunny16 interceptor has handed to `reportAxiosError`, whether reported or dropped
// as noise. The interceptor sees an exhausted request's error once per retry attempt, and an uncaught
// one goes on to the Vue, promise, router or plain reporters, which would each report it again.
const claimedAxiosErrors = new WeakSet<object>()

/** Claims `error` for the interceptor's report: true the first time, false once claimed. */
export function claimAxiosErrorReport(error: object): boolean {
  if (claimedAxiosErrors.has(error)) return false

  claimedAxiosErrors.add(error)
  return true
}

function wasClaimedAxiosError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && claimedAxiosErrors.has(error)
}

class AppErrorService {
  private static instance: AppErrorService | null = null
  private reportToNewRelic = false
  private newRelicInstance: Agent | null = null

  private constructor() {}

  static getInstance(): AppErrorService {
    if (!AppErrorService.instance) {
      AppErrorService.instance = new AppErrorService()
    }
    return AppErrorService.instance
  }

  initialize(reportToNewRelic: boolean, newRelicInstance: Agent | null): void {
    this.reportToNewRelic = reportToNewRelic
    this.newRelicInstance = newRelicInstance
  }

  /**
   * The single route from this service to the Browser Agent.
   *
   * Every report goes through here so that marking it as ours cannot be
   * forgotten by a new `report*` method — an unmarked report is indistinguishable
   * from the agent's own auto-capture and gets dropped by the `setErrorHandler`
   * filter in `main.ts`.
   */
  private notice(error: Error, customAttributes: Record<string, unknown>): void {
    if (!this.reportToNewRelic || !this.newRelicInstance) return

    reportedErrors.add(error)
    this.newRelicInstance.noticeError(error, customAttributes)
  }

  reportVueError(
    err: unknown,
    vm: ComponentPublicInstance | null,
    info: string,
    customAttributes?: Record<string, unknown>
  ) {
    if (wasClaimedAxiosError(err)) return

    const message = getErrorMessage(err)
    const normalized: NormalizedError = {
      source: 'vue',
      message,
      reason: err,
      stack: getErrorStack(err)
    }
    if (isNoise(normalized)) return

    let vueComponentName: string | null = null

    if (vm?.$options?.name) {
      vueComponentName = vm.$options.name
    }

    if (import.meta.env.MODE !== 'production') {
      console.error('Error:', err)
      console.error('Vue component:', vm)
      console.error('Additional info:', info)
      if (vueComponentName) {
        console.error('Vue component Name:', vueComponentName)
      }
    }

    this.notice(toReportableError(err, message), {
      vueComponentName,
      ...customAttributes
    })
  }

  reportUnhandledPromiseRejection(
    event: PromiseRejectionEvent,
    customAttributes?: Record<string, unknown>
  ) {
    const reason: unknown = event.reason
    if (wasClaimedAxiosError(reason)) return

    const message = getErrorMessage(reason)
    const normalized: NormalizedError = {
      source: 'promise',
      message,
      reason,
      stack: getErrorStack(reason)
    }
    if (isNoise(normalized)) return

    if (import.meta.env.MODE !== 'production') {
      console.error('Unhandled Promise Rejection:', reason)
    }

    const errorToReport = toReportableError(reason, message)
    errorToReport.name = 'UnhandledPromiseRejection'

    this.notice(errorToReport, {
      originalReason: message,
      ...customAttributes
    })
  }

  reportWindowError(
    message: string | Event,
    url?: string,
    line?: number,
    col?: number,
    err?: unknown,
    customAttributes?: Record<string, unknown>
  ) {
    const eventMessage = typeof message === 'string' ? message : ''
    const messageText = isErrorLike(err) ? err.message : eventMessage
    const normalized: NormalizedError = {
      source: 'window',
      message: messageText,
      reason: err,
      stack: getErrorStack(err),
      url,
      line
    }
    if (isNoise(normalized)) return

    if (import.meta.env.MODE !== 'production') {
      console.error('JS Error:', err || message)
      if (url) console.error('URL:', url)
      if (line !== undefined) console.error('Line:', line, 'Column:', col)
    }

    // `err` is `event.error` verbatim — whatever was thrown, not necessarily an
    // `Error`. A third-party tag doing `throw 'boom'` makes it a string, and
    // `notice` marks every report in a `WeakSet`, which throws on a primitive:
    // the real error would be lost and the browser would re-fire `error` with
    // that `TypeError` in its place. Objects pass through untouched, because
    // identity is what lets `main.ts` recognise the agent's duplicate of this
    // same throw.
    const reportable =
      typeof err === 'object' && err !== null
        ? (err as Error)
        : new Error(messageText || String(message))

    this.notice(reportable, {
      errorUrl: url,
      errorLine: line,
      errorColumn: col,
      ...customAttributes
    })
  }

  reportError(err: unknown, customAttributes?: Record<string, unknown>) {
    if (wasClaimedAxiosError(err)) return

    if (import.meta.env.MODE !== 'production') {
      console.error('JS Error:', err)
    }

    this.notice(toReportableError(err, getErrorMessage(err)), { ...customAttributes })
  }

  reportAxiosError(err: AxiosError, customAttributes?: Record<string, unknown>) {
    const responseData: unknown = err.response?.data
    const statusCode = err.response?.status
    const requestUrl = err.config?.url
    const errorMessage = this.getAxiosErrorMessage(responseData, statusCode, err.message)
    const normalized: NormalizedError = {
      source: 'axios',
      message: errorMessage,
      reason: err,
      stack: err.stack ?? null,
      axiosCode: err.code,
      axiosStatus: statusCode,
      axiosOriginalMessage: err.message
    }
    if (isNoise(normalized)) return

    if (import.meta.env.MODE !== 'production') {
      console.error('Axios Error:', err)
      console.error('Error Message:', errorMessage)
    }

    const errorToReport = new Error(errorMessage)
    errorToReport.name = 'AxiosError'

    this.notice(errorToReport, {
      originalMessage: err.message,
      statusCode,
      requestUrl,
      responseData: typeof responseData === 'string' ? responseData : JSON.stringify(responseData),
      requestBody: this.getCartRequestBody(err),
      ...customAttributes
    })
  }

  /**
   * The request body for cart API calls only, truncated to fit a New Relic attribute. Other
   * endpoints (checkout, orders) carry customer details and are deliberately excluded.
   */
  private getCartRequestBody(err: AxiosError): string | undefined {
    const fullUrl = `${err.config?.baseURL ?? ''}${err.config?.url ?? ''}`
    if (!/\/storefront\/cart\//.test(fullUrl)) return undefined

    const data: unknown = err.config?.data
    if (data === undefined || data === null) return undefined

    const body = typeof data === 'string' ? data : JSON.stringify(data)
    return body.length > 4000 ? `${body.slice(0, 4000)}…` : body
  }

  private getAxiosErrorMessage(
    responseData: unknown,
    statusCode: number | undefined,
    fallbackMessage: string
  ): string {
    let apiErrorMessage: string | null = null
    if (typeof responseData === 'string' && responseData.length > 0) {
      apiErrorMessage = responseData
    } else if (responseData && typeof responseData === 'object') {
      // Common API response formats: { message: "..." }, { error: "..." }, { Message: "..." }
      const data = responseData as Record<string, unknown>
      if (typeof data.message === 'string' && data.message) apiErrorMessage = data.message
      else if (typeof data.error === 'string' && data.error) apiErrorMessage = data.error
      else if (typeof data.Message === 'string' && data.Message) apiErrorMessage = data.Message
    }

    if (apiErrorMessage) return apiErrorMessage
    if (statusCode) return `HTTP ${statusCode}: ${fallbackMessage}`
    return fallbackMessage
  }

  reportVueRouterError(err: unknown, customAttributes?: Record<string, unknown>) {
    if (wasClaimedAxiosError(err)) return

    if (import.meta.env.MODE !== 'production') {
      console.error('Vue Router Error:', err)
    }

    this.notice(toReportableError(err, getErrorMessage(err)), { ...customAttributes })
  }
}

export const appErrorService = AppErrorService.getInstance()
